<template>
  <!-- The v-if checks for equality of the current results and potential new queries.-->
  <ag-grid-vue v-if="JSON.stringify(row_keys) == JSON.stringify(Object.keys(current_snvs))" class="ag-theme-alpine"
    :columnDefs="column_defs" :defaultColDef="default_cols" :rowData="row_data" :rowStyle="row_style"
    :style="`height: ${grid_height}px; width: auto;`" rowHeight="60" :alwaysShowHorizontalScroll="false"
    :pagination="true" :paginationPageSize="14" :gridOptions="gridOptions" @grid-ready="onGridReady" id="snv-table" />
</template>

<script setup lang="js">
import { AgGridVue } from 'ag-grid-vue3';
import { onMounted, onUnmounted, ref, computed, watch, render, h } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import PathoBar from '@/components/tables/PathoBar.vue';
import StarButton from '@/components/tables/StarButton.vue';
import { Icon } from '@iconify/vue';
import TooltipHeader from '@/components/tables/TooltipHeader.vue';
import useAGGrid from '@/methods/ag_grid_toolbox';

const store = useStore();
const selected_variants = computed(() => store.state.presentation_selected_variants);
const current_snvs = computed(() => {
  let snvs = store.getters.snvs;
  return Object.fromEntries(Object.entries(snvs).filter((variant) => {
    if (!props.preferSelected || Object.keys(selected_variants.value).length == 0) return true;
    return (selected_variants.value[variant[0]] != undefined);
  }))
});
const current_patient_data = computed(() => store.getters.biomarkersForCurrentPatient)
const flipstate = computed(() => store.state.presentation_flipstate);

const router = useRouter();
const column_defs = computed(() => store.state.ag_grid_coldefs[props?.table_id]);
const row_data = computed(() => store.state.ag_grid_rowdata[props?.table_id]);
const row_keys = computed(() => store.state.ag_grid_rowkeys[props?.table_id]);
const devmode = computed(() => store.state.devmode);

const default_cols = ref()
/* Grid height is set to 14*60 + 100 (14 rows)*/
const grid_height = computed(() => {
  var temp_height = Math.min(940, (row_keys.value.length + 1) * 60 + 40);
  if (props?.maxHeight) {
    temp_height = Math.min(props?.maxHeight, temp_height);
    gridOptions.api?.paginationSetPageSize(Math.floor((temp_height - 100) / 60))
  }
  return temp_height;
})

const gridOptions = {};

/**
 * @prop preferSelected - If this is set to true, only selected variants will be shown in the table.
 * @prop showSelectColumn - Dictates whether to show the select column (i.e. the blue star) on this table.
 * @prop usePatientData - If set to true, will not show data from the SNVs in store, but from a specific patient.
 * @prop maxHeight - Can be passed to limit this table's maximum height (in px).
 * @prop table_id - ID for the table. Important for HTML query selections, so it should be unique.
 */
const props = defineProps({
  preferSelected: Boolean,
  showSelectColumn: Boolean,
  usePatientData: Boolean,
  maxHeight: Number,
  table_id: String,
})

/**
 * When this table is loaded, the following steps are performed:
 * 1. Either patient data or SNVs are picked (list_to_use) based on the props.
 * 2. Default column definitions, set in ag_grid_toolbox.js, are used.
 * 3. Specific column definitions for SNVs are created.
 * 4. The chosen data (list_to_use) is turned into an AG Grid RowData object.
 * 5. Columns are resized and the page is set to 0.
 * 6. Event listeners for sorting and resizing are added.
 * 7. Table entries are sorted based on the "REVEL" column, descending.
 */
onMounted(() => {
  var list_to_use = pickVariantList();
  if (list_to_use) {
    default_cols.value = useAGGrid.createDefaultColDefs();
    createColDefs();
    SNVtoRowData(pickVariantList());
    gridOptions.api?.sizeColumnsToFit();
    gridOptions.api?.paginationGoToPage(0);
    window.addEventListener('resize', resizeGrid);
    window.addEventListener('sort-grid', (e) => onSort(e));
  }
});

/**
 * Event listeners for sorting and resizing are removed on unmounting the component.
 */
onUnmounted(() => {
  gridOptions.api?.destroy();
  window.removeEventListener('resize', resizeGrid);
  window.removeEventListener('sort-grid', (e) => onSort(e))
})

/**
 * Called when the sort-grid window event is caught. Applies a specific sort to the grid and refreshes it.
 * Will sort based on a targeted column based on a provided sorting method (asc, desc, none)
 * @param {CustomEvent} e - Event that contains sorting information (column, desired order)
 */
const onSort = (e) => {
  if (e.detail.table == props?.table_id)
    sortTableBy(e.detail.column, e.detail.order);
}

/**
 * Once the grid is ready, it is refreshed and the select column is hidden if indicated by the props.
 * Then, it is resized.
 */
const onGridReady = () => {
  //gridOptions.api?.refreshCells();
  if (!props.showSelectColumn) {
    gridOptions.columnApi?.setColumnVisible('select', false)
  }
  //sortTableBy('REVEL', 'desc');
  sortTableBy('ACMG', 'desc');
  resizeGrid();
  store.dispatch('setAGGShown', {table_id: props?.table_id, show_grid: true});
}

/**
 * Resizes/re-renders specific elements of the grid based on the screen width.
 * Sizes should be set in accordance with any media queries.
 */
const resizeGrid = () => {
  gridOptions.api?.sizeColumnsToFit();
  let current_coldefs = gridOptions.api?.getColumnDefs();

  // Sizes below 1300
  if (window.innerWidth <= 1350) {
    current_coldefs.forEach((def, _) => {
      def.headerName = shortened_headers[def.colId];
      def.flex = 1;
      def.headerComponentParams.title = shortened_headers[def.colId];
      if (def.colId == 'select') def.headerComponent = null;
      if (['revel', 'alphamissense', 'mvp', 'primateai', 'loftool'].includes(def.colId)) {
        def.cellRenderer = (param) => renderPathoVal(param, long_headers[def.colId]);
        def.cellStyle = { justifyContent: 'center' };
      }
      if (def.colId == 'clinvar') def.cellStyle = { ...def.cellStyle, wordBreak: "break-all" };
      if (def.colId == 'cosmic') def.cellStyle = { ...def.cellStyle, wordBreak: "break-all" };
      if (def.colId == 'variant') def.cellRenderer = (param) => renderVariant(param, "small");

      // Sizes below 1000
      if (window.innerWidth < 1000) {
        def.headerComponent = null;
        if (def.colId == 'details') {
          def.width = '60';
          def.maxWidth = '60';
          def.minWidth = '60';
          def.cellRenderer = (param) => addRouterLink(param, 'small')
        }
        if (def.colId == 'select') {
          def.width = '50';
          def.maxWidth = '50';
          def.minWidth = '50';
        }
      }
      // Sizes between 1000 and 1300
      else {
        if (def.colId == 'details') {
          def.width = '80';
          def.maxWidth = '80';
          def.minWidth = '80';
          def.cellRenderer = (param) => addRouterLink(param, 'small')
        }
        if (def.colId == 'select') {
          def.width = '60';
          def.maxWidth = '60';
          def.minWidth = '60';
        }
      }
    });
  }
  // Normal size (> 1300)
  if (window.innerWidth > 1350) {
    current_coldefs.forEach((def, index) => {
      def.headerComponent = TooltipHeader;
      def.headerName = long_headers[def.colId];
      def.flex = ['evidence_exact', 'evidence_other', 'alphamissense'].includes(def.colId) ? 1.25 : 1;
      if (window.innerWidth <= 1750) { /* Hardcoded extra rules for AlphaMissense and the two evidence columns*/
        if (def.colId == 'alphamissense') def.headerComponentParams.title = "AlphaMis";
        if (def.colId == 'evidence_exact') def.headerComponentParams.title = 'Ev exact';
        if (def.colId == 'evidence_other') def.headerComponentParams.title = 'Ev other';
      }
      if (window.innerWidth <= 1600) { /* Hardcoded extra rules for PAI and VUSPredict */
        if (['primateai'].includes(def.colId))
          def.headerComponentParams.title = shortened_headers[def.colId];
      }
      if (['revel', 'alphamissense', 'mvp', 'primateai', 'loftool'].includes(def.colId)) {
        def.cellRenderer = (param) => renderPathoBar(param, def.headerName);
      }
      if (def.colId == 'details') {
        def.width = '120';
        def.maxWidth = '120';
        def.minWidth = '120';
        def.cellRenderer = (param) => addRouterLink(param, 'full')
      }
      if (def.colId == 'select') {
        def.width = '100';
        def.maxWidth = '100';
        def.minWidth = '100';
      }
      if (def.colId == 'cosmic') def.cellStyle = { ...def.cellStyle, wordBreak: "normal" };
      if (def.colId == 'clinvar') def.cellStyle = { ...def.cellStyle, wordBreak: "normal" };
      if (def.colId == 'variant') def.cellRenderer = (param) => renderVariant(param, "full")

    })
  }
  gridOptions.api?.setColumnDefs(current_coldefs);
  if (store.getters.getProcessState('load-results') == '')
    var hm = 0;
  //setTimeout(function() { gridOptions.api?.redrawRows(); }, 0)
}



const pickVariantList = () => {
  return (props?.usePatientData ? current_patient_data.value.biomarkers : current_snvs.value)
}


/**
 * Creates table column definitions. These are specific for this table.
 * Saves the definitions in the column_defs ref (see below) and applies them to the grid.
 * If keys and key_values refs are defined below, they are used to automatically create some column definitions.
 */
const createColDefs = () => {

  let new_column_defs = [];

  // Selection Column
  if (props.showSelectColumn && devmode.value) {
    let s_width = (window.innerWidth < 1300 ? (window.innerWidth < 1000 ? '50' : '60') : '100')
    new_column_defs.push({
      headerName: 'Select', headerClass: 'presentation-header', colId: 'select',
      cellRenderer: (param) => { //TODO Replace
        let container = document.createElement("div")
        const vsb = h(StarButton, {
          id: param?.data.variant.row1, onToggle: (id) => toggleSelectedVariant(id),
          selected: Object.keys(selected_variants.value).includes(param?.data.variant.row1)
        });
        render(vsb, container);
        return container;
      },
      autoHeight: true, flex: 0, width: s_width, minWidth: s_width, maxWidth: s_width, pinned: 'left', sortable: 'false',
      headerComponent: TooltipHeader, headerComponentParams: { title: 'Select', table: props?.table_id }, cellStyle: { justifyContent: "center" }
    })
  }

  //Exchange
  new_column_defs.push({
    headerName: 'Variant', filter: 'agTextColumnFilter', cellStyle: { 'lineHeight': '16pt' }, headerClass: 'presentation-header-left',
    cellRenderer: (param) => renderVariant(param, "full"),
    headerComponent: TooltipHeader, headerComponentParams: { title: 'Variant', tooltip_id: 'variant_snv', table: props?.table_id }, colId: 'variant', wrapText: true,
    cellStyle: { wordBreak: "normal", lineHeight: "110%", textAlign: "left" }, valueGetter: (param) => { return param.data.variant.row2 }
  })

  //Cosmic
  //new_column_defs.push({
  //  colId: 'cosmic', headerName: 'Role', headerClass: 'presentation-header-left', field: 'cosmic', wrapText: true,
  //  cellStyle: { lineHeight: '110%', textAlign: "left" }, headerComponent: TooltipHeader, headerComponentParams: { title: 'Role', table: props?.table_id, tooltip_id: 'cosmic_role' },
  //  comparator: (valueA, valueB) => useAGGrid.clinvarComparator(valueA, valueB)
  //});

  //ClinVar
  new_column_defs.push({
    colId: 'clinvar', headerName: 'ClinVar', headerClass: 'presentation-header-left', field: 'ClinVar', wrapText: true,
    cellStyle: { lineHeight: '110%', textAlign: "left" }, headerComponent: TooltipHeader, headerComponentParams: { title: 'ClinVar', table: props?.table_id },
    comparator: (valueA, valueB) => useAGGrid.clinvarComparator(valueA, valueB)
  });


  //AlphaMissense, Revel, VusPredict, MVP, PrimateAI
  for (let header of key_headers.value) {
    new_column_defs.push({
      headerName: header,
      field: header,
      headerClass: 'presentation-header', cellStyle: { justifyContent: "center" }, flex: (header == 'AlphaMissense') ? 1.25 : 1,
      cellRenderer: (param) => window.innerWidth < 1300 ? renderPathoVal(param, header) : renderPathoBar(param, header),
      autoHeight: true, colId: header.toLowerCase(), headerComponent: TooltipHeader, headerComponentParams: { title: header, table: props?.table_id }
    })
  }

  // Evidence: Exact and Other
  if (!props?.usePatientData) {
    new_column_defs.push({
      headerName: 'Evidence (exact)', colId: 'evidence_exact', headerClass: 'presentation-header', type: 'number', field: 'evidence_exact', flex: 1.25,
      headerComponent: TooltipHeader, headerComponentParams: { title: 'Evidence (exact)', colid: 'evidence_exact', tooltip_id: 'evidence_exact', table: props?.table_id },
      cellStyle: { justifyContent: "center" }
    });

    new_column_defs.push({
      headerName: 'Evidence (other)', colId: 'evidence_other', headerClass: 'presentation-header', field: 'evidence_other', flex: 1.25,
      headerComponent: TooltipHeader, headerComponentParams: { title: 'Evidence (other)', colid: 'evidence_other', tooltip_id: 'evidence_other', table: props?.table_id },
      cellStyle: { justifyContent: "center" }
    });
  }
  else
    new_column_defs.push({
      headerName: 'Comment', sortable: 'true',
      field: 'comment', headerClass: 'presentation-header',
      headerComponent: TooltipHeader, headerComponentParams: { title: 'Comment', table: props?.table_id }, colId: 'comment',
    });

  // ACMG Score
  new_column_defs.push({
    colId: 'acmg', headerName: 'ACMG', headerClass: 'presentation-header-left', field: 'acmg', wrapText: true,
    cellStyle: { lineHeight: '110%', textAlign: "left" }, headerComponent: TooltipHeader, headerComponentParams: { title: 'ACMG', table: props?.table_id },
  });



  // Details
  let d_width = (window.innerWidth < 1300 ? (window.innerWidth < 1000 ? '60' : '80') : '120')
  new_column_defs.push({
    headerName: 'Details', sortable: 'false', cellRenderer: (param) => addRouterLink(param, window.innerWidth < 1300 ? 'small' : 'full'),
    headerClass: 'presentation-header', pinned: 'right',
    width: d_width, maxWidth: d_width, minWidth: d_width, colId: 'details', cellStyle: { justifyContent: "center" },
    headerComponent: TooltipHeader, headerComponentParams: { title: 'Details', table: props?.table_id }, flex: 0,
  });

  store.dispatch('setAGGColdefs', { defs: new_column_defs, id: props?.table_id });
  gridOptions.api?.setColumnDefs(new_column_defs);
}

/**
 * Turns the chosen data (either store-based SNVs or patient data) into rows for the AG Grid.
 * Uses two refs (see below): row_data and keys.
 * @param {object} list_to_use - Either current_snvs or current_patient_data should be passed here.
 */
const SNVtoRowData = (list_to_use) => {
  if (!list_to_use || !Object.keys(list_to_use).length) return [];
  let new_row_data = [];
  if (!Object.keys(list_to_use)) return;
  for (let variant of Object.keys(list_to_use)) {

    //First entry is always the variant (both genomic and protein-level exchanges)
    let amino_exchange = useAGGrid.resolveDeepProperty(variant + '.UTA_Adapter.gene_name', list_to_use) + ':' +
      useAGGrid.resolveDeepProperty(variant + '.UTA_Adapter.variant_exchange', list_to_use);
    let new_row = { 'variant': { row1: variant, row2: amino_exchange } };

    //COSMIC cancer gene census: role in cancer
    //try {
    //    let cosmic = useAGGrid.resolveDeepProperty(variant + '.cosmic[0].ROLE_IN_CANCER', list_to_use);
    //    if (cosmic) new_row['cosmic'] = cosmic;
    //} catch(e) {
    //    console.log(e)
    //}

    //ClinVar
    let clinvar = useAGGrid.resolveDeepProperty(variant + '.clinvar.CLNSIG', list_to_use);
    if (clinvar) new_row['ClinVar'] = clinvar;

    //Evidence
    let evidence_set = countEvidence(variant, list_to_use);
    if (evidence_set) {
      new_row['evidence_exact'] = evidence_set[0];
      new_row['evidence_other'] = evidence_set[1];
    }

    //Other columns are filled dynamically based on the refs defined below
    for (let descriptor of keys.value) {
      let result = useAGGrid.resolveDeepProperty(variant + '.' + descriptor, list_to_use)
      if (result) {
        new_row[key_headers.value[keys.value.indexOf(descriptor)]] = result;
      }
    }

    // Score
    try {
        let acmg = useAGGrid.resolveDeepProperty(variant + '.onkopus_interpreter.acmg.score', list_to_use);
        if (acmg) new_row['acmg'] = acmg;
    } catch(e) {
        console.log(e)
    }

    //Finally, generate link for details button and marked status:
    new_row['details_link'] = { path: `${store.getters.getRouterBaseURL}/show-variant-details-q/${amino_exchange}` };
    new_row['marked'] = false;
    new_row_data.push(new_row);
  }
  store.dispatch('setAGGRowdata', { data: new_row_data, id: props?.table_id });
  store.dispatch('setAGGRowkeys', { keys: Object.keys(list_to_use), id: props?.table_id });
}

/**
 * Toggles whether an SNV is part of the 'selected variants/genes' subset or not.
 * @param {String} id - Variant identifier
 */
const toggleSelectedVariant = async (id) => {
  store.dispatch('togglePresentationVariantSelected', id);
  //gridOptions.api?.refreshCells();
}

/**
 * Adds a router-link button to the details column.
 * @param {object} param - AG Grid parameters 
 */
const addRouterLink = (param, size = 'full') => {
  const link = document.createElement("div");
  link.innerText = "Details";

  if (param.data.variant.row2 != "undefined:undefined") {
    link.classList.add("details-button");
  } else {
    link.classList.add("details-button-disabled");
  }
  link.classList.add("text-button");
  if (size == 'small') {
    link.innerText = 'D.'
    link.classList.add("details-button-small")
  }
  link.addEventListener("click", e => {
    e.preventDefault();
    if (!store.getters.resultsLoaded) return;
    if (param.data.variant.row2 != "undefined:undefined") {
      router.push(param.data.details_link.path);
    }
  });
  buttons.value.push(link);
  return link;
}

/**
 * Renders a pathogenicity prediction score without a bar (for small screen sizes).
 * @param {Object} param - AG Grid parameters 
 * @param {String} header - The according header (e.g. 'ClinVar') to identify the score 
 */
const renderPathoVal = (param, header) => {
  let container = document.createElement("div")
  if (!param.data[header]) {
    if (store.getters.getProcessState('load-results'))
      render(h(Icon, { icon: 'eos-icons:three-dots-loading', width: '48px', height: '48px', class: 'load-icon' }), container);
    else {
      container = null;
    }
  }
  else {
    container.innerHTML = `<span>${param?.data[header]}</span>`
    container.style.color = param?.data[header] > 0.5 || param?.data[header] < 0 ? 'var(--pathogenic)' : 'var(--darkest)'
  }
  return container;
}

/**
 * Renders a pathogenicity prediction score inside a horizontal score bar (for bigger screen sizes).
 * @param {Object} param - AG Grid parameters 
 * @param {String} header - The according header (e.g. 'ClinVar') to identify the score 
 */
const renderPathoBar = (param, header) => {
  let container = document.createElement("div")
  if (!param.data[header]) {
    container = null;
  }
  else {
    //if (header == 'VUSPredict')
      //render(h(PathoBar, { value: param?.data[header], min: -1, max: 1, mode: 'vuspredict' }), container);
    //else
      render(h(PathoBar, { value: param?.data[header], min: 0, max: 1 }), container);
  }
  return container;
}

/**
 * Dictates how to render the "variant" column. On small screen sizes only the AA exchange is shown.
 * @param {Object} param - AG Grid parameters 
 * @param {String} size - Either "full" (shows both AA and genomic variant) or "small" (only shows AA)
 */
const renderVariant = (param, size = "full") => {
  if (size == "full")
    return (param?.data.variant.row2 == ':' || param?.data.variant.row2.search('undefined') != -1) ?
      `No amino ex. provided<br>${(param?.data.variant.row1.length > 20 ? (truncateVariant(param?.data.variant.row1)) : param?.data.variant.row1)}`
      : param?.data.variant.row2 + '<br>'
      + (param?.data.variant.row1.length > 20 ? (truncateVariant(param?.data.variant.row1)) : param?.data.variant.row1)
  else {
    if (param?.data.variant.row2 == ':' || param?.data.variant.row2.search('undefined') != -1) return "No amino ex. provided";
    let var_split = param?.data.variant.row2.split(":");
    return var_split[0] + '<br>' + var_split[1];
  }
}

/**
 * Counts the total clinical evidence entries from OncoKB, Civic and MetaKB to show in the table.
 * Returns two values as an array: The number of exact matches and the number of other matches.
 * @param {Object} variant - The variant in question
 * @param {Object} list_to_use - Either current_snvs or current_patient_data should be passed here
 */
const countEvidence = (variant, list_to_use) => {
  if (!list_to_use || Object.keys(list_to_use).length == 0) return 0;
  else {
    let interpretation_count = 0;
    let interpretation_count_exact = 0;
    for (let db_key of ['oncokb.oncokb_features_norm', 'metakb.metakb_features_norm', 'civic.civic_features_norm']) {
      try {
        interpretation_count += useAGGrid.resolveDeepProperty(variant + '.' + db_key + '.any_mutation_in_gene', list_to_use).length;
        interpretation_count += useAGGrid.resolveDeepProperty(variant + '.' + db_key + '.same_position', list_to_use).length;
        interpretation_count += useAGGrid.resolveDeepProperty(variant + '.' + db_key + '.same_position_any_mutation', list_to_use).length;
        interpretation_count_exact += useAGGrid.resolveDeepProperty(variant + '.' + db_key + '.exact_match', list_to_use).length;
      }
      catch { }
    }
    return [interpretation_count_exact, interpretation_count];
  }

}

/**
 * Truncates a variant string if its deemed to be too long to show in the front end.
 * @param {String} variant_string - The string in question
 */
const truncateVariant = (variant_string) => {
  const variant_regex = "(?<chr>chr[(0-9)+,X,Y]+):(?<pos>[0-9]+)(?<from>[a-zA-Z,\-]+)>(?<to>[a-zA-Z,\-]+)";
  var regexp = new RegExp(variant_regex);
  var result = regexp.exec(variant_string);
  if (!result) return variant_string;
  else {
    return result[1] + ":[...]" +
      (result[3].length > 10 ? result[3].substring(9, result[3].length) : result[3]) + ">" +
      (result[4].length > 10 ? result[4].substring(9, result[4].length) : result[4]);
  }
}

/**
 * Sorts the table.
 * @param {String} column - The column to sort by (preferably ID, but title should also work) 
 * @param {String} order - one of 'asc', 'desc' or 'none'
 */
const sortTableBy = (column, order) => {
  console.log(`sorting ${column} and ${order}`)
  gridOptions.columnApi?.applyColumnState({
    state: [{ colId: column.toLowerCase(), sort: order }],
    defaultState: { sort: null },
  });
}

/**
 * Updates current SNVs on results update, if the table is used to display SNV data (~ usePatientData == false)
 * To check if the results changed, the result object keys are compared
 */
watch(() => current_snvs.value, () => {
  if (props?.usePatientData) return;
  if (Object.keys(current_snvs.value).length > 0 && (row_keys.value != Object.keys(current_snvs.value) || !row_keys.value)) {
    SNVtoRowData(current_snvs.value);
    gridOptions.api?.refreshCells();
    gridOptions.api?.sizeColumnsToFit();
    gridOptions.api?.paginationGoToPage(0);
  }
});

/**
 * Updates current patient biomarker data, if the table is used to display patient data (~ usePatientData == true)
 */
watch(() => current_patient_data.value, () => {
  if (!props?.usePatientData) return;
  if (Object.keys(current_patient_data.value).length > 0 && (row_keys.value != Object.keys(current_patient_data.value) || !row_keys.value)) {
    SNVtoRowData(current_patient_data.value.biomarkers);
    gridOptions.api?.refreshCells();
    gridOptions.api?.sizeColumnsToFit();
    gridOptions.api?.paginationGoToPage(0);
  }
});

/**
 * Redraws the grid once the loading process stops.
 * Somehow refreshCells() is not enough here, so all rows have to be redrawn.
 */
watch(() => store.getters.resultsLoaded, (loadstate) => {
  if (loadstate) {
    gridOptions.api?.refreshCells();
    gridOptions.api?.sizeColumnsToFit();
  }
});

/**
 * Makes this table "flippable" using websocket commands.
 */
watch(() => flipstate.value, (flp) => {
  if (!flp.flipstate) return;
  var current_page = gridOptions.api?.paginationGetCurrentPage();
  var total_pages = gridOptions.api?.paginationGetTotalPages();
  if (flp.flip == 'forward') {
    gridOptions.api?.paginationGoToPage((current_page + 1 == total_pages ? 0 : current_page + 1));
    store.dispatch('performPresentationFlipForward', false);
  } else if (flp.flip == 'backward') {
    gridOptions.api?.paginationGoToPage((current_page - 1 == -1 ? total_pages - 1 : current_page - 1));
    store.dispatch('performPresentationFlipBackward', false);
  }
})

/** This ref contains all keys from the SNV object that will be checked.
 * Keys that don't exist in the object are simply ignored.
 */
const keys = ref(['revel.Score', 'alphamissense.score', 'primateai.Score', 'dbnsfp.MVP_score_aggregated_value']);
/**
 * This ref contains header names for the keys in the same order.
 */
const key_headers = ref(['REVEL', 'AlphaMissense', 'PrimateAI', 'MVP']);
const shortened_headers = {
  'mvp': 'MVP',
  'loftool': 'LT',
  'revel': 'RV',
  'clinvar': 'CV',
  'variant': 'V.',
  'select': 'S.',
  'primateai': 'PA',
  'evidence_exact': 'Ex.',
  'evidence_other': 'Oth.',
  'details': 'Dt.',
  'alphamissense': 'AM',
  'acmg': 'ACMG',
}
const long_headers = {
  'mvp': 'MVP',
  'loftool': 'LoFtool',
  'revel': 'REVEL',
  'clinvar': 'ClinVar',
  'variant': 'Variant',
  'select': 'Select',
  'primateai': 'PrimateAI',
  'evidence_exact': 'Evidence (exact)',
  'evidence_other': 'Evidence (other)',
  'details': 'Details',
  'alphamissense': 'AlphaMissense',
  'acmg': 'ACMG'
}

const column_configuration = {
  '_flex': 1,
}

const buttons = ref([]);
</script>

<style>
.ag-theme-alpine {
  --ag-border-color: var(--dark-grey);
  overflow: hidden;
  --details-min-width: 150;
}

.ag-row .ag-cell {
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
}

::-webkit-scrollbar {
  -webkit-appearance: none;
  width: 7px;
}

.details-button {
  display: flex;
  background-color: #fff;
  color: #000;
  border-radius: 5px;
  padding-left: 16px;
  padding-right: 16px;
  width: fit-content;
  height: 32px;
  justify-items: center;
  align-items: center;
  margin: auto;
  position: absolute;
  transform: translate(-50%, -50%);
  top: 50%;
  left: 50%;
  box-shadow: rgba(14, 30, 37, 0.12) 0px 2px 4px 0px, rgba(14, 30, 37, 0.32) 0px 2px 2px 0px;
  font-family: var(--pure-material-font, "Roboto", "Segoe UI", BlinkMacSystemFont, system-ui, -apple-system);
}

.details-button:hover {
  background-color: #f3f3f3;
  cursor: pointer;
}

.details-button-disabled {
  display: flex;
  background-color: #f0f0f0;
  color: #d5d5d5;
  border-radius: 5px;
  padding-left: 16px;
  padding-right: 16px;
  width: fit-content;
  height: 32px;
  justify-items: center;
  align-items: center;
  margin: auto;
  position: absolute;
  transform: translate(-50%, -50%);
  top: 50%;
  left: 50%;
  font-family: var(--pure-material-font, "Roboto", "Segoe UI", BlinkMacSystemFont, system-ui, -apple-system);
}

.presentation-header * {
  text-align: center;
  justify-content: center;
}

.presentation-header-left * {
  text-align: left;
  justify-content: left;
}

.load-icon {
  margin-left: 24px;
}

.details-button-small {
  width: 40px !important;
  padding: 0 !important;
  justify-content: center !important;
}

@media (max-width: 1300px) {

  div.ag-theme-alpine div.ag-row {
    font-size: 0.7rem !important;
  }

}

@media (max-width: 1000px) {

  div.ag-theme-alpine div.ag-row {
    font-size: 0.6rem !important;
  }

}
</style>

